export const bookContent = {
  title: "Breakthrough",
  subtitle: "A Faith-Based Guide to Healing and Restoration",
  author: "Janelle McGowan-Charles",
  businessName: "Purpose Reclaimed Coaching",
  copyright: `© ${new Date().getFullYear()} Janelle McGowan-Charles, Purpose Reclaimed Coaching. All rights reserved.`,
  introduction: `Dear Sister in Christ,

If you're holding this guide, you've already taken the first courageous step toward healing. What happened to you was not your fault. The trauma you experienced does not define who you are or who you can become.

This guide is designed to walk with you through the darkness and into the light of God's healing love. Through scripture, reflection, and prayer, we will journey together from the fog of trauma toward the fullness of life God has planned for you.

You are seen. You are loved. You are worthy of healing.`,
  chapters: [
    {
      id: 1,
      title: "The Fog of Trauma",
      subtitle: "Acknowledging Where You Are",
      image: "https://images.unsplash.com/photo-1747044118927-7abb4b3fc99e?crop=entropy&cs=srgb&fm=jpg&q=85",
      scripture: {
        text: "He heals the brokenhearted and binds up their wounds.",
        reference: "Psalm 147:3 (NIV)"
      },
      content: `Trauma often feels like walking through a thick fog. You know there's something beyond it, but you can't quite see it. The memories, the pain, the confusion—they cloud everything.

Sexual abuse leaves deep wounds that can feel impossible to heal. You may experience feelings of shame, guilt, fear, or numbness. Some days, simply getting through feels like an enormous victory.

Here's the truth that cuts through the fog: God sees you in this place. He doesn't look away from your pain. He moves toward you with compassion and healing in His hands.

The fog doesn't have to be your permanent home. This is where your breakthrough begins.`,
      affirmation: "I acknowledge my pain, and I trust that God is present with me in this moment.",
      quote: "Healing doesn't mean the damage never existed. It means the damage no longer controls our lives.",
      journalQuestions: [
        "What does the 'fog' feel like in your daily life? How does it affect your thoughts, emotions, and relationships?",
        "When do you feel closest to God? When do you feel furthest away?",
        "What would it look like for you to experience breakthrough? What would be different?"
      ],
      prayer: `Heavenly Father,

I come before You today acknowledging my pain and my need for Your healing. I've been walking through this fog for so long, Lord, and I'm tired.

Thank You for seeing me. Thank You for not turning away from my brokenness. I ask that You would begin to lift this fog, one day at a time.

Help me to feel Your presence in my darkest moments. Remind me that I am not alone in this journey.

In Jesus' name, Amen.`
    },
    {
      id: 2,
      title: "Breaking Free from Shame",
      subtitle: "Releasing What Was Never Yours to Carry",
      image: "https://images.unsplash.com/photo-1751840345002-c30d69763d69?crop=entropy&cs=srgb&fm=jpg&q=85",
      scripture: {
        text: "Therefore, there is now no condemnation for those who are in Christ Jesus.",
        reference: "Romans 8:1 (NIV)"
      },
      content: `Shame tells us we ARE the problem. It whispers that we are fundamentally broken, unworthy, and unlovable because of what happened to us.

But here's the truth: What happened TO you is not WHO you are.

Shame is a heavy cloak that many survivors wear without realizing it. It affects how we see ourselves, how we relate to others, and how we approach God. We may feel unworthy of love, convinced that if people really knew us, they would reject us.

God's word speaks a different truth over you. You are not condemned. You are not rejected. The shame you carry was never meant to be yours.

Jesus took our shame upon Himself at the cross. He absorbed it, conquered it, and offers us freedom in return. This isn't about pretending the trauma didn't happen—it's about refusing to let it define your worth.`,
      affirmation: "The shame I feel is not my identity. I am a beloved daughter of the King, worthy of love and belonging.",
      quote: "Shame says 'I am bad.' Grace says 'Something bad happened to you, but you are deeply loved.'",
      journalQuestions: [
        "What shame messages have you believed about yourself because of what happened to you?",
        "How has shame affected your relationships with others? With God?",
        "Write a letter to your younger self. What would you want her to know about her worth?"
      ],
      prayer: `Lord Jesus,

I bring my shame to You today—all the lies I've believed about myself, all the ways I've hidden and withdrawn because I felt unworthy.

Your Word says there is no condemnation for those in Christ Jesus. Help me to believe this truth in the deepest parts of my heart.

Remove this heavy cloak of shame that I've been wearing. Replace it with Your robe of righteousness. Help me see myself as You see me—loved, chosen, and precious.

Thank You for taking my shame to the cross. Help me walk in the freedom You purchased for me.

In Your precious name, Amen.`
    },
    {
      id: 3,
      title: "Releasing Guilt and Self-Blame",
      subtitle: "Understanding What Wasn't Your Fault",
      image: "https://images.unsplash.com/photo-1757330878738-04e93fc5eb2f?crop=entropy&cs=srgb&fm=jpg&q=85",
      scripture: {
        text: "Cast all your anxiety on him because he cares for you.",
        reference: "1 Peter 5:7 (NIV)"
      },
      content: `One of the cruelest effects of sexual abuse is the guilt that survivors often carry. "If only I had..." "Why didn't I..." "I should have..."

Let this truth sink deep into your heart: IT WAS NOT YOUR FAULT.

Regardless of the circumstances, regardless of what you were wearing, where you were, or what you did or didn't do—you did not cause this to happen. The responsibility lies entirely with the person who made the choice to harm you.

Guilt can become a prison of our own making. We replay scenarios, imagining different outcomes. We punish ourselves for things we had no control over. This false guilt robs us of the freedom God wants us to experience.

God invites you to cast your burdens on Him—including the burden of misplaced guilt. He doesn't ask you to carry this weight. He asks you to release it.`,
      affirmation: "What happened to me was not my fault. I release the false guilt I've carried and accept God's truth about my innocence.",
      quote: "You cannot heal what you will not acknowledge. But you don't have to carry what was never yours to hold.",
      journalQuestions: [
        "What specific guilt have you carried related to your trauma? Write it out.",
        "If a dear friend told you her story and it was identical to yours, what would you say to her about her guilt?",
        "What does it feel like to hear 'It was not your fault'? What resistance or relief comes up?"
      ],
      prayer: `Father God,

I confess that I have blamed myself for things that were not my fault. I have carried guilt that was never mine to bear.

Today, I choose to release this burden to You. I lay down the 'if onlys' and the 'why didn't I's. I refuse to punish myself any longer for someone else's sin.

Help me distinguish between true conviction from Your Spirit and false guilt from the enemy. Free me from this prison of self-blame.

Thank You for Your care for me. Thank You for being strong enough to carry what I cannot.

In Jesus' name, Amen.`
    },
    {
      id: 4,
      title: "Escaping the Victim Mentality",
      subtitle: "From Survivor to Overcomer",
      image: "https://images.unsplash.com/photo-1570631810485-911f9516d8d8?crop=entropy&cs=srgb&fm=jpg&q=85",
      scripture: {
        text: "No, in all these things we are more than conquerors through him who loved us.",
        reference: "Romans 8:37 (NIV)"
      },
      content: `There's an important distinction between acknowledging that you were victimized and living with a victim identity.

What happened to you was real, and you have every right to grieve and process it. But staying stuck in victimhood can become a prison that prevents you from moving into the abundant life God has for you.

The victim mentality keeps us looking backward, focused on what was done to us and who did it. It can lead to patterns of blame, helplessness, and giving away our power to others or circumstances.

God calls you to something more. He calls you from victim to survivor to overcomer to conqueror.

This shift isn't about minimizing your pain or pretending it didn't happen. It's about refusing to let your past determine your future. It's about reclaiming the identity and authority that God gives you as His daughter.

You are more than what happened to you. You are more than a conqueror through Christ.`,
      affirmation: "I am not defined by what was done to me. I am defined by who God says I am—more than a conqueror.",
      quote: "The past cannot be changed, but how you carry it can be transformed. Victim is a circumstance. Overcomer is a choice.",
      journalQuestions: [
        "In what ways have you noticed yourself stuck in victim mentality? How has it affected your daily life?",
        "What would it look like to move from 'survivor' to 'overcomer' in your specific situation?",
        "What strengths have you developed because of what you've been through? How might God want to use these?"
      ],
      prayer: `Mighty God,

Thank You for calling me more than a conqueror. Help me to believe and walk in this truth.

I don't want to stay stuck in the past. I don't want to be defined by what happened to me. I want to move forward into the life You have planned for me.

Give me the courage to take responsibility for my healing journey. Show me where I've given away my power to bitterness, blame, or helplessness.

Empower me to walk in victory. Help me see myself as an overcomer, not a victim.

In the name of Jesus, my Victorious King, Amen.`
    },
    {
      id: 5,
      title: "The Journey to Forgiveness",
      subtitle: "Finding Freedom Through Letting Go",
      image: "https://images.unsplash.com/photo-1564639869273-7a2a7f4f9e5e?crop=entropy&cs=srgb&fm=jpg&q=85",
      scripture: {
        text: "Bear with each other and forgive one another if any of you has a grievance against someone. Forgive as the Lord forgave you.",
        reference: "Colossians 3:13 (NIV)"
      },
      content: `Forgiveness is perhaps the most challenging—and most liberating—part of the healing journey.

Let's be clear about what forgiveness is NOT:
• It's not saying what happened was okay
• It's not forgetting or minimizing the abuse
• It's not reconciling with or trusting your abuser
• It's not a one-time event you can force

Forgiveness IS:
• Releasing your right to revenge
• Cutting the emotional chains that tie you to your abuser
• A process that happens over time
• A gift you give yourself, not your abuser
• An act of obedience that brings freedom

When we refuse to forgive, we drink poison expecting the other person to die. Unforgiveness keeps us bound to our abuser in bitterness and pain.

Forgiveness doesn't mean the person who hurt you gets away with it. God is the righteous judge. Forgiveness means you're no longer willing to be imprisoned by what they did.

This is a journey, not a destination. Some days you'll feel like you've forgiven completely; other days the anger will return. Keep bringing it to God. Keep choosing to release. Freedom comes gradually, then suddenly.`,
      affirmation: "I choose to begin the journey of forgiveness—not for my abuser's sake, but for my own freedom and obedience to God.",
      quote: "Forgiveness is not an occasional act; it is a permanent attitude. It is giving up all hope of having had a better past.",
      journalQuestions: [
        "What has prevented you from moving toward forgiveness? What fears or objections come up?",
        "How has unforgiveness affected your life? Your health? Your relationships? Your relationship with God?",
        "What would it look like to take one small step toward forgiveness today? What support do you need?"
      ],
      prayer: `Lord Jesus,

This is hard. Forgiveness feels impossible when I think about what was done to me.

But I know that unforgiveness is a prison, and I don't want to live there anymore. I don't want to be chained to my abuser for the rest of my life.

I'm not there yet, but I'm willing to begin the journey. Help me forgive—not in my own strength, but through Your power working in me.

Heal the deep wounds that make forgiveness feel impossible. Show me Your perspective on my abuser as a broken person who will face Your justice.

I release my right to revenge. I trust You to be the righteous judge. Help me walk in freedom.

In Your name, Amen.`
    },
    {
      id: 6,
      title: "Seeing Your True Potential",
      subtitle: "Embracing God's Vision for Your Life",
      image: "https://images.unsplash.com/photo-1749731043489-5e87d7d55123?crop=entropy&cs=srgb&fm=jpg&q=85",
      scripture: {
        text: "For we are God's handiwork, created in Christ Jesus to do good works, which God prepared in advance for us to do.",
        reference: "Ephesians 2:10 (NIV)"
      },
      content: `You have come so far, dear sister.

Now it's time to lift your eyes from where you've been and look toward where God is leading you. You are not defined by your trauma. You are defined by your Creator.

God sees you not as damaged goods, but as His masterpiece. Before you were born, He planned beautiful works for you to do. Your past pain does not disqualify you from your divine purpose—in fact, God often uses our deepest wounds to bring healing to others.

Consider these truths about how God sees you:

• You are fearfully and wonderfully made (Psalm 139:14)
• You are chosen, holy, and dearly loved (Colossians 3:12)
• You have been given a spirit of power, love, and self-discipline (2 Timothy 1:7)
• God has plans to prosper you and give you hope and a future (Jeremiah 29:11)
• Nothing can separate you from God's love (Romans 8:38-39)

Your story isn't over. In fact, your breakthrough is just beginning. The very things that the enemy meant for evil, God can use for incredible good in your life and the lives of others.

You are entering a new season. Walk boldly into it. You are a daughter of the King, and your crown has been waiting for you.`,
      affirmation: "I am God's masterpiece, created for a purpose. My past does not determine my future. I walk boldly into God's plans for my life.",
      quote: "Your story isn't finished. The same God who brought you through the fire is preparing you for something beautiful beyond it.",
      journalQuestions: [
        "What dreams or desires have you put aside because of your trauma? What would you pursue if you believed nothing was holding you back?",
        "How might God use your story to help others? What unique understanding and compassion has your journey given you?",
        "Write a vision statement for your life going forward. Who do you want to become? What do you want to accomplish for God's kingdom?"
      ],
      prayer: `Father God,

Thank You for bringing me through. Thank You for not giving up on me, even when I wanted to give up on myself.

I receive Your vision of who I am—Your masterpiece, created for good works that You prepared in advance. Help me to walk confidently in this identity.

Open my eyes to the potential You see in me. Show me the gifts and callings You've placed inside me. Use my story to bring hope and healing to others.

I step into this new season with faith. I release the old identity of brokenness and receive Your declaration that I am made new.

Thank You for the breakthrough. Thank You for what lies ahead.

In the mighty name of Jesus, Amen.`,
      closingBenediction: `Dear Sister,

As you close this guide, know that your journey doesn't end here—it's truly just beginning.

You have taken courageous steps toward healing. You have confronted shame, released guilt, moved from victim to overcomer, and begun the journey of forgiveness. Most importantly, you have caught a glimpse of who God says you truly are.

Healing isn't linear. There will be hard days ahead. But you now have tools, truths, and most importantly, a Savior who walks with you every step of the way.

Remember:
• You are loved beyond measure
• You are not what happened to you
• You are more than a conqueror
• Your best days are ahead

Go forth in freedom, dear one. Your breakthrough has begun.

With love and prayers for your journey,
Your Sister in Christ`
    }
  ]
};
